import React, { memo, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Button } from '../../../aem-core-components';
import RadioButton from '../../global/atoms/radioButton/RadioButton';
import AlertIcon from '../../../resources/images/alert-triangle-red.svg';
import { getBusinessAccountScreenConfigs, getCreateAccountObject } from '../utils/guidedUtil';
import useCreateAccount from './useCreateAccount';
import { LoadingIndicator } from '../../../aem-core-components';
import { PAYMENT_PREFERENCE_CREDIT, BUSINESS_ACCOUNT, PAYMENT_PREFERENCE_POD, PAYMENT_PREFERENCE_TYPE } from '../constants';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../constants/analyticsConstants/Ecommerce';
import { useAnalyticsContext } from '../../../config/GoogleTagManagerEvents';
import './createAccount.scss';

const PaymentPreference = () => {
    const createAccountConfig = getCreateAccountObject();
    const businessAccountConfig = getBusinessAccountScreenConfigs();

    const {
        formState = {},
        handleInputChange = () => {},
        handleSubmit = () => {},
        onPaymentPreferenceContinue = () => {},
        isLoading = false,
    } = useCreateAccount();

    const {
        businessAccountDesc = '',
        businessAccountTitleLabel = '',
        businessAccountTypes = []
    } = businessAccountConfig || {};

    // businessAccountTypeTitle, // businessAccountTypeDesc
    const creditAccountData = businessAccountTypes?.[0] || {};
    const podAccountData = businessAccountTypes?.[1] || {};

    const {
        continueButtonLabel = '',
        requiredFieldsLabel = '',
    } = createAccountConfig || {};

    const { sendEventsForGuidedRegistrationFormViewed, sendEventsForPageViewed } = useAnalyticsContext();

    useEffect(() => {
        window.scrollTo(0, 0);
        sendEventsForGuidedRegistrationFormViewed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_VIEWED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_FORM_NAME
        );
    }, []);

    return (
        <div className={isLoading ? 'loading_overlay_background' : ''}>
            <section className="guidedForm__section">
                <div className="container">
                    <div className="row guidedForm__row pod_guidedForm__row">
                        <div className="col-lg-6">
                            <div className="form-heading guidedForm__heading">
                                <h2 className="guidedForm__title">{businessAccountTitleLabel}</h2>
                                <small className="guidedForm__require-field-text">{requiredFieldsLabel}</small>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <fieldset className="guidedForm__fieldset">
                                    <legend
                                        className={`guidedForm__legend 
                                        ${formState?.error?.[PAYMENT_PREFERENCE_TYPE] ? 'legend-error' : ''} requiredSymbol`}
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(businessAccountDesc)}}
                                    />
                                    <div className="guidedForm__content">
                                        <div className="guidedForm__radio guidedForm__contents">
                                            <div role="radiogroup" className="d-flex guidedForm__radiogroup">
                                                <RadioButton
                                                    id={`${PAYMENT_PREFERENCE_TYPE}-${PAYMENT_PREFERENCE_CREDIT}`}
                                                    name={PAYMENT_PREFERENCE_TYPE}
                                                    value={PAYMENT_PREFERENCE_CREDIT}
                                                    checked={formState?.data?.[PAYMENT_PREFERENCE_TYPE] === PAYMENT_PREFERENCE_CREDIT}
                                                    handleRadioChange={handleInputChange}
                                                    label={creditAccountData?.businessAccountTypeTitle || 'Credit'}
                                                    customClassName={
                                                        formState?.error?.[PAYMENT_PREFERENCE_TYPE] ? 'error_radio' : ''
                                                    }
                                                    dataTestId={`${PAYMENT_PREFERENCE_TYPE}-${PAYMENT_PREFERENCE_CREDIT}`}
                                                />
                                            </div>
                                            <small className="guidedForm__radiobtn-heading">
                                                {creditAccountData?.businessAccountTypeDesc || ''}
                                            </small>
                                        </div>
                                        <div className="guidedForm__radio guidedForm__contents">
                                            <div role="radiogroup" className="d-flex guidedForm__radiogroup">
                                                <RadioButton
                                                    id={`${PAYMENT_PREFERENCE_TYPE}-${PAYMENT_PREFERENCE_POD}`}
                                                    name={PAYMENT_PREFERENCE_TYPE}
                                                    value={PAYMENT_PREFERENCE_POD}
                                                    checked={formState?.data?.[PAYMENT_PREFERENCE_TYPE] === PAYMENT_PREFERENCE_POD}
                                                    handleRadioChange={handleInputChange}
                                                    label={podAccountData?.businessAccountTypeTitle || 'Pay as you go'}
                                                    customClassName={
                                                        formState?.error?.[PAYMENT_PREFERENCE_TYPE] ? 'error_radio' : ''
                                                    }
                                                    dataTestId={`${PAYMENT_PREFERENCE_TYPE}-${PAYMENT_PREFERENCE_POD}`}
                                                />
                                            </div>
                                            <small className="guidedForm__radiobtn-heading">
                                                {podAccountData?.businessAccountTypeDesc || ''}
                                            </small>
                                        </div>
                                        {formState?.error?.[PAYMENT_PREFERENCE_TYPE] && (
                                            <div className="guidedForm__error_account">
                                                <AlertIcon />
                                                <span className="guidedForm__error_input">
                                                    {formState?.error?.[PAYMENT_PREFERENCE_TYPE]}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </fieldset>
                                <Button
                                    data-testid="continue-button"
                                    className="button button-primary button-block"
                                    type="button"
                                    onClick={onPaymentPreferenceContinue}
                                    buttonAriaLabel="Continue">
                                    {continueButtonLabel}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
            {isLoading && (
                <div className="globalmodal__overlay loading_overlay">
                    <LoadingIndicator />
                </div>
            )}
        </div>
    );
};

export default memo(PaymentPreference);
